// Environment configuration for API endpoints

export interface Config {
  apiBaseUrl: string;
  wsBaseUrl: string;
  environment: "development" | "staging" | "production";
  features: {
    enableWebSocket: boolean;
    enableRealTimeUpdates: boolean;
    enableDebugMode: boolean;
  };
}

// Get the current environment
function getEnvironment(): "development" | "staging" | "production" {
  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "production" ? "production" : "development";
  }

  const hostname = window.location.hostname;

  if (hostname === "knakkert.nl") {
    return "production";
  } else if (hostname.includes("staging") || hostname.includes("dev")) {
    return "staging";
  } else if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "development";
  }

  return "production"; // Default fallback
}

// Environment-specific configurations
const configs: Record<string, Config> = {
  development: {
    apiBaseUrl: "http://localhost:8787",
    wsBaseUrl: "ws://localhost:8787",
    environment: "development",
    features: {
      enableWebSocket: true,
      enableRealTimeUpdates: true,
      enableDebugMode: true,
    },
  },
  staging: {
    apiBaseUrl: "https://worker.knakkert.nl",
    wsBaseUrl: "wss://worker.knakkert.nl",
    environment: "staging",
    features: {
      enableWebSocket: true,
      enableRealTimeUpdates: true,
      enableDebugMode: true,
    },
  },
  production: {
    apiBaseUrl: "https://knakkert.nl",
    wsBaseUrl: "wss://knakkert.nl",
    environment: "production",
    features: {
      enableWebSocket: true,
      enableRealTimeUpdates: true,
      enableDebugMode: false,
    },
  },
};

// Get the current configuration
export const config: Config = configs[getEnvironment()];

// Helper functions for API endpoints
export const API_ENDPOINTS = {
  // Workflow endpoints
  workflows: `${config.apiBaseUrl}/api/workflows`,
  createWorkflow: `${config.apiBaseUrl}/api/workflows`,
  startWorkflow: (id: string) =>
    `${config.apiBaseUrl}/api/workflows/${id}/start`,
  stopWorkflow: (id: string) => `${config.apiBaseUrl}/api/workflows/${id}/stop`,
  getWorkflow: (id: string) => `${config.apiBaseUrl}/api/workflows/${id}`,
  getWorkflowEvents: (id: string) =>
    `${config.apiBaseUrl}/api/workflows/${id}/events`,

  // Agent endpoints
  agents: `${config.apiBaseUrl}/api/agents`,
  getAgent: (id: string) => `${config.apiBaseUrl}/api/agents/${id}`,

  // WebSocket endpoint
  websocket: `${config.wsBaseUrl}/ws`,
};

// Export utility functions
export const isProduction = () => config.environment === "production";
export const isDevelopment = () => config.environment === "development";
export const isStaging = () => config.environment === "staging";

// Feature flags
export const FEATURES = config.features;

// Default configuration values
export const DEFAULT_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  requestTimeout: 30000,
  websocketReconnectDelay: 5000,
  maxWebsocketReconnectAttempts: 5,
  eventBatchSize: 10,
  maxConcurrentWorkflows: isProduction() ? 100 : 10,
  maxEventsPerWorkflow: 1000,
  websocketTimeout: isProduction() ? 300000 : 30000, // 5 minutes in prod, 30s in dev
};

// Console logging helper that respects debug mode
export const debugLog = (message: string, ...args: any[]) => {
  if (FEATURES.enableDebugMode) {
    console.log(`[AI-Agent-Waterfall] ${message}`, ...args);
  }
};

export const debugError = (message: string, error?: any) => {
  if (FEATURES.enableDebugMode) {
    console.error(`[AI-Agent-Waterfall] ERROR: ${message}`, error);
  }
};

export const debugWarn = (message: string, ...args: any[]) => {
  if (FEATURES.enableDebugMode) {
    console.warn(`[AI-Agent-Waterfall] WARNING: ${message}`, ...args);
  }
};
